import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  Box,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DateTime } from 'luxon';
import styles from './ContestComissionDetailsModal.styles';

const useStyles = makeStyles(styles);

const formatDate = date => {
  if (!date) {
    return '-';
  }
  try {
    return DateTime.fromJSDate(new Date(date)).toFormat('dd/MM/yyyy HH:mm');
  } catch {
    return date;
  }
};

const formatCurrency = value => {
  if (!value && value !== 0) {
    return '-';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

export function ContestComissionDetailsModal({ open, onClose, data }) {
  const classes = useStyles();

  const invoiceMetadata = data?.invoiceMetadata || {};
  const invoiceItems = data?.invoiceItems || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalhes da Contestação</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Informações Básicas</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>ID do Log</Typography>
                <Typography className={classes.fieldValue}>{data?.id || '-'}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Data de Criação</Typography>
                <Typography className={classes.fieldValue}>{formatDate(data?.createdAt)}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Parceiro</Typography>
                <Typography className={classes.fieldValue}>{data?.partnerName || '-'}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Invoice ID</Typography>
                <Typography className={classes.fieldValue}>{data?.partnerInvoiceId || '-'}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Parceiro ID</Typography>
                <Typography className={classes.fieldValue}>{data?.partnerId || '-'}</Typography>
              </div>
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Mensagem</Typography>
          <Typography className={classes.messageText}>{data?.message || '-'}</Typography>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Informações da Invoice</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Valor</Typography>
                <Typography className={classes.fieldValue}>{formatCurrency(invoiceMetadata.amount)}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Status</Typography>
                <Chip
                  label={invoiceMetadata.status || '-'}
                  size="small"
                  className={classes.statusChip}
                  color="primary"
                  variant="outlined"
                />
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Mês de Referência</Typography>
                <Typography className={classes.fieldValue}>
                  {invoiceMetadata.referenceDate
                    ? DateTime.fromJSDate(new Date(invoiceMetadata.referenceDate)).toFormat('MM/yyyy')
                    : '-'}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Número da Invoice</Typography>
                <Typography className={classes.fieldValue}>{invoiceMetadata.invoiceNumber || '-'}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Número da OC</Typography>
                <Typography className={classes.fieldValue}>{invoiceMetadata.purchaseOrderNumber || '-'}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Status da OC</Typography>
                <Typography className={classes.fieldValue}>{invoiceMetadata.purchaseOrderStatus || '-'}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Data de Emissão</Typography>
                <Typography className={classes.fieldValue}>{formatDate(invoiceMetadata.issueDate)}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Data de Pagamento</Typography>
                <Typography className={classes.fieldValue}>{formatDate(invoiceMetadata.paymentDate)}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.field}>
                <Typography className={classes.fieldLabel}>Status Oracle</Typography>
                <Typography className={classes.fieldValue}>{invoiceMetadata.oracleInvoiceStatus || '-'}</Typography>
              </div>
            </Grid>
          </Grid>
        </Box>

        {invoiceItems && invoiceItems.length > 0 ? (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Itens da Invoice ({invoiceItems.length})</Typography>
              {invoiceItems.map((item, index) => {
                const itemKey = item?.id || item?.comissioningLeadId || item?.partnerId || `item-${index}`;
                return (
                  <Accordion key={itemKey} className={classes.invoiceItemAccordion} defaultExpanded={false}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Item {index + 1} {item?.type ? `- ${item.type}` : ''}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
                      <Grid container spacing={2} style={{ width: '100%' }}>
                        {(item?.customerName || item?.comissioningLead?.customerName) && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Cliente</Typography>
                              <Typography className={classes.fieldValue}>
                                {item?.customerName || item?.comissioningLead?.customerName}
                              </Typography>
                            </div>
                          </Grid>
                        )}
                        {(item?.identificationNumber || item?.comissioningLead?.identificationNumber) && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>CPF/CNPJ</Typography>
                              <Typography className={classes.fieldValue}>
                                {item?.identificationNumber || item?.comissioningLead?.identificationNumber}
                              </Typography>
                            </div>
                          </Grid>
                        )}
                        {item?.installmentAmount !== undefined && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Valor da Parcela</Typography>
                              <Typography className={classes.fieldValue}>
                                {formatCurrency(item.installmentAmount)}
                              </Typography>
                            </div>
                          </Grid>
                        )}
                        {item?.amount !== undefined && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Valor</Typography>
                              <Typography className={classes.fieldValue}>{formatCurrency(item.amount)}</Typography>
                            </div>
                          </Grid>
                        )}
                        {item?.partnerName && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Parceiro</Typography>
                              <Typography className={classes.fieldValue}>{item.partnerName}</Typography>
                            </div>
                          </Grid>
                        )}
                        {item?.totalAmount !== undefined && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Valor Total</Typography>
                              <Typography className={classes.fieldValue}>{formatCurrency(item.totalAmount)}</Typography>
                            </div>
                          </Grid>
                        )}
                        {(item?.distribuidora || item?.comissioningLead?.energyUtility) && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Distribuidora</Typography>
                              <Typography className={classes.fieldValue}>
                                {item?.distribuidora || item?.comissioningLead?.energyUtility}
                              </Typography>
                            </div>
                          </Grid>
                        )}
                        {item?.totalClients !== undefined && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Total de Clientes</Typography>
                              <Typography className={classes.fieldValue}>{item.totalClients}</Typography>
                            </div>
                          </Grid>
                        )}
                        {item?.totalVolume !== undefined && (
                          <Grid item xs={6}>
                            <div className={classes.field}>
                              <Typography className={classes.fieldLabel}>Volume Total</Typography>
                              <Typography className={classes.fieldValue}>
                                {item.totalVolume.toLocaleString('pt-BR')} kWh
                              </Typography>
                            </div>
                          </Grid>
                        )}
                      </Grid>
                      <pre
                        style={{
                          fontSize: '11px',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          backgroundColor: '#f5f5f5',
                          padding: '12px',
                          borderRadius: '4px',
                          width: '100%',
                          maxHeight: '400px',
                          overflow: 'auto',
                          boxSizing: 'border-box',
                        }}
                      >
                        {JSON.stringify(item, null, 2)}
                      </pre>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </>
        ) : (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Itens da Invoice</Typography>
              <Typography className={classes.fieldValue} style={{ fontStyle: 'italic', color: '#999' }}>
                Nenhum item encontrado
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ContestComissionDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.string,
    partnerId: PropTypes.string,
    partnerName: PropTypes.string,
    partnerInvoiceId: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.string,
    invoiceMetadata: PropTypes.object,
    invoiceItems: PropTypes.array,
  }),
};

ContestComissionDetailsModal.defaultProps = {
  data: null,
};
